import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Loader2, Check, X } from 'lucide-react'

interface ManagerActionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onApprove: (notes: string) => Promise<void>
  onReject: (reason: string) => Promise<void>
  itemTitle: string
  itemType: string
}

export function ManagerActionDialog({
  open,
  onOpenChange,
  onApprove,
  onReject,
  itemTitle,
  itemType,
}: ManagerActionDialogProps) {
  const [action, setAction] = useState<'approve' | 'reject' | null>(null)
  const [notes, setNotes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    if (!action) return
    
    setIsSubmitting(true)
    try {
      if (action === 'approve') {
        await onApprove(notes)
      } else {
        if (!notes.trim()) {
          alert('Please provide a reason for rejection')
          setIsSubmitting(false)
          return
        }
        await onReject(notes)
      }
      setNotes('')
      setAction(null)
      onOpenChange(false)
    } catch (error) {
      console.error('Manager action failed:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setNotes('')
    setAction(null)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] max-w-md">
        <DialogHeader>
          <DialogTitle>People Leader Action</DialogTitle>
          <DialogDescription>
            Review and {action === 'approve' ? 'acknowledge' : action === 'reject' ? 'reject' : 'acknowledge or reject'} this {itemType}: <strong>{itemTitle}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!action && (
            <div className="flex gap-2">
              <Button
                onClick={() => setAction('approve')}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <Check className="h-4 w-4 mr-2" />
                Acknowledge
              </Button>
              <Button
                onClick={() => setAction('reject')}
                variant="destructive"
                className="flex-1"
              >
                <X className="h-4 w-4 mr-2" />
                Reject
              </Button>
            </div>
          )}

          {action && (
            <>
              <div className="space-y-2">
                <Label htmlFor="notes">
                  {action === 'approve' ? 'People Leader Notes' : 'Rejection Reason *'}
                  {/* Note: 'approve' is the internal action key; user-facing copy says Acknowledge */}
                </Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={
                    action === 'approve'
                      ? 'Add any feedback or validation notes...'
                      : 'Please explain why this is being rejected...'
                  }
                  rows={4}
                  required={action === 'reject'}
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setAction(null)}
                  disabled={isSubmitting}
                >
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting || (action === 'reject' && !notes.trim())}
                  className={action === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
                  variant={action === 'reject' ? 'destructive' : 'default'}
                >
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Confirm {action === 'approve' ? 'Acknowledgement' : 'Rejection'}
                </Button>
              </DialogFooter>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
